/* eslint-disable no-useless-escape */
import * as React from "react";
import styled from "styled-components";
import { useValidator } from "../../../utils/validators";
import { lang } from "../../../constants";

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const Label = styled.div<{ 
  color?: string; 
  fontSize?: number; 
  fontWeight?: number; 
  isUppercase?: boolean; 
}>`
  color: ${props => props.color || '#333'};
  font-size: ${props => props.fontSize ? `${props.fontSize}px` : '16px'};
  font-weight: ${props => props.fontWeight || 400};
  text-transform: ${props => props.isUppercase ? 'uppercase' : 'none'};
  margin-bottom: 8px;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledInput = styled.input.withConfig({
  shouldForwardProp: (prop) => !['bgColor', 'borderColor', 'borderRadius', 'fontWeight', 'fontSize', 'color', 'width', 'height', 'padding', 'labelColor', 'labelFontSize', 'labelFontWeight'].includes(prop as string),
})<{
  bgColor?: string;
  borderColor?: string;
  borderRadius?: number;
  fontWeight?: number;
  fontSize?: number;
  color?: string;
  width?: number;
  height?: number;
  padding?: string;
  labelColor?: string;
  labelFontSize?: number;
  labelFontWeight?: number;
  inputMode?: string;
}>`
  background-color: ${props => props.bgColor || 'transparent'};
  border: 1px solid ${props => props.borderColor || '#ccc'};
  border-radius: ${props => props.borderRadius ? `${props.borderRadius}px` : '4px'};
  font-weight: ${props => props.fontWeight || 400};
  font-size: ${props => props.fontSize ? `${props.fontSize}px` : '14px'};
  color: ${props => props.color || '#333'};
  width: ${props => props.width ? `${props.width}px` : '100%'};
  height: ${props => props.height ? `${props.height}px` : 'auto'};
  padding: ${props => props.padding || '8px 12px'};

  &::placeholder {
    font-size: ${props => props.labelFontSize ? `${props.labelFontSize}px` : '16px'};
    color: ${props => props.labelColor || '#B0B0B0'};
    font-weight: ${props => props.labelFontWeight || 400};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.borderColor || '#e39435'};
    box-shadow: 0 0 0 2px rgba(227, 148, 53, 0.2);
  }
`;

const AdornmentSpan = styled.span<{
  fontSize?: number;
  fontWeight?: number;
  color?: string;
  marginRight?: string;
  marginLeft?: string;
}>`
  font-size: ${props => props.fontSize ? `${props.fontSize}px` : '14px'};
  font-weight: ${props => props.fontWeight || 400};
  color: ${props => props.color || '#333'};
  margin-right: ${props => props.marginRight};
  margin-left: ${props => props.marginLeft};
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  height: 24px;
  font-size: 14px;
  margin-top: 4px;
`;

const EmptySpace = styled.div`
  height: 8px;
`;

interface IProps {
  titleEN: string;
  titleCN: string;
  value: string | number;
  callback: (value: string) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  decimalPlaces?: number;
  isRequired?: boolean;
  width?: number;
  height?: number;
  padding?: string;
  borderColor?: string;
  borderRadius?: number;
  fontWeight?: number;
  fontSize?: number;
  color?: string;
  bgColor?: string;
  placeholder?: string;
  startAdornment?: string;
  symbolFontWeight?: number;
  symbolFontSize?: number;
  symbolColor?: string;
  adornmentPosition?: string;
  label?: string;
  labelColor?: string;
  labelFontSize?: number;
  labelFontWeight?: number;
  isUppercase?: boolean;
  inputMode?: string;
}

export default function CustomNumberField(props: IProps) {
  const decimalPlaces = props.decimalPlaces || 2;
  
  const validators = {
    field: (value: string) => validateNumber(value),
  };

  const initialValues = {
    field: {
      value: props.value?.toString() || "",
      accTxtBoxisValid: true,
      isTouched: false,
    },
  };

  const { values, errors, handleChange, setValues } = useValidator(
    initialValues,
    validators
  );

  // Sync internal state with props.value changes
  React.useEffect(() => {
    setValues(prev => ({
      ...prev,
      field: {
        ...prev.field,
        value: props.value?.toString() || "",
      }
    }));
  }, [props.value, setValues]);

  const sanitizeFloatInput = (input: string): string => {
    // Allow only digits, decimal point, and minus sign
    let cleaned = input.replace(/[^0-9\.\-]/g, "");
    
    // Handle negative numbers - only allow minus at the beginning
    const hasLeadingMinus = cleaned.startsWith("-");
    cleaned = cleaned.replace(/-/g, "");
    if (hasLeadingMinus) {
      cleaned = "-" + cleaned;
    }
    
    // Handle decimal points - only allow one decimal point
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      cleaned = parts[0] + "." + parts.slice(1).join("");
    }
    
    // Limit decimal places
    if (parts.length === 2 && parts[1].length > decimalPlaces) {
      cleaned = parts[0] + "." + parts[1].substring(0, decimalPlaces);
    }
    
    return cleaned;
  };

  const customHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const sanitizedValue = sanitizeFloatInput(value);
    
    // Create a synthetic event with the sanitized value
    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        value: sanitizedValue
      }
    };
    
    // Call the original handleChange with sanitized value
    handleChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
    
    // Call the callback with the sanitized value
    props.callback(sanitizedValue);
  };

  const validateNumber = (value: string): boolean => {
    if (!props.isRequired && (value === "" || value === undefined)) {
      return true;
    }
    
    const numValue = parseFloat(value);
    
    // Check if it's a valid number
    if (isNaN(numValue)) {
      return false;
    }
    
    // Check min/max constraints
    if (props.min !== undefined && numValue < props.min) {
      return false;
    }
    
    if (props.max !== undefined && numValue > props.max) {
      return false;
    }
    
    return true;
  };

  const keyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    props.callback(target.value);
  };

  return (
    <>
      <Container>
        {props.label && (
          <Label
            color={props.labelColor}
            fontSize={props.labelFontSize}
            fontWeight={props.labelFontWeight}
            isUppercase={props.isUppercase}
          >
            {props.label}
          </Label>
        )}
        <InputWrapper>
          {props.startAdornment && !props.adornmentPosition && (
            <AdornmentSpan
              fontSize={props.symbolFontSize}
              fontWeight={props.symbolFontWeight}
              color={props.symbolColor}
              marginRight="8px"
            >
              {props.startAdornment}
            </AdornmentSpan>
          )}

          <StyledInput
            type="number"
            inputMode={props.inputMode}
            min={props.min || 0}
            max={props.max}
            value={values.field.value}
            name="field"
            onChange={customHandleChange}
            onKeyUp={keyUp}
            disabled={props.disabled}
            placeholder={props.placeholder || (lang() === "en" ? props.titleEN : props.titleCN)}
            bgColor={props.bgColor}
            borderColor={props.borderColor}
            borderRadius={props.borderRadius}
            fontWeight={props.fontWeight}
            fontSize={props.fontSize}
            color={props.color}
            width={props.width}
            height={props.height}
            padding={props.padding}
            labelColor={props.labelColor}
            labelFontSize={props.labelFontSize}
            labelFontWeight={props.labelFontWeight}
          />
          
          {props.startAdornment &&
            props.adornmentPosition &&
            props.adornmentPosition === "right" && (
              <AdornmentSpan
                fontSize={props.symbolFontSize}
                fontWeight={props.symbolFontWeight}
                color={props.symbolColor}
                marginLeft="8px"
              >
                {props.startAdornment}
              </AdornmentSpan>
            )}
        </InputWrapper>
      </Container>

      {errors.field && values.field.isTouched ? (
        <ErrorMessage>
          {lang() === "en"
            ? "Please enter a valid " + props.titleEN + "."
            : "请输入有效的" + props.titleCN + "。"}
        </ErrorMessage>
      ) : (
        <EmptySpace />
      )}
    </>
  );
}
